import type { ReactElement } from 'react';
import { DropdownMenuItem } from '@radix-ui/react-dropdown-menu';
import { Vendor, getCurrentVendor } from '@studio/design-system/themes/vendor.js';
import { ContentIris } from './vendor/iris/content-iris.jsx';
import { ContentHive } from './vendor/hive/content-hive.jsx';
import type { ContentProps } from './types.js';
import { Callout } from '@studio/design-system/callout';
import { useRegistryListAlert } from '#~/auth/use-registry-list-alert.js';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';

/** `<Content>` component that renders vendor-specific UI. */
export const Content = (props: ContentProps): ReactElement => {
  const isIris = getCurrentVendor() === Vendor.IRIS;
  const listAlert = useRegistryListAlert('organizations');

  if (listAlert.visible)
    return (
      <Callout
        severity='warning'
        role='status'
        title="Couldn't load your organizations"
        {...DataSTag(Sel.SettingsSwitcher, 'load_error')}
      >
        <DropdownMenuItem
          asChild
          disabled={listAlert.retrying}
          onSelect={(event) => event.preventDefault()}
        >
          <button
            type='button'
            disabled={listAlert.retrying}
            aria-busy={listAlert.retrying}
            onClick={() => {
              void listAlert.retry();
            }}
            className='focus-visible:ring-primary/50 mt-12 flex items-center gap-8 rounded border border-muted bg-base-100 px-12 py-6 text-foreground focus-visible:ring-2 disabled:opacity-50'
            {...STagNew(Sel.SettingsSwitcher, 'load_retry')}
          >
            {listAlert.retrying && (
              <span
                aria-hidden='true'
                className='size-[14px] animate-spin rounded-full border-2 border-muted border-t-foreground'
              />
            )}
            Try again
          </button>
        </DropdownMenuItem>
      </Callout>
    );

  return isIris ? <ContentIris {...props} /> : <ContentHive {...props} />;
};
